import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';

// PM Analitik Faz 2 Plan 3 (spec §6) — İK Operasyon Panosu veri katmanı. Pano yalnız mevcut uçları okur:
//   GET v1/performance-analytics/metrics?codes=…&compare=7d   (KPI'lar; karşılaştırma dönemi için aynı kodlar o dönemle)
//   GET v1/performance-analytics/breakdown?codes=…&dimension=… (paneller)
//   GET v1/performance-analytics/tasks?overdue=true&sort=overdueDays&order=desc&pageSize=5
//   GET v1/performance-insights?…  ·  GET v1/performance-actions?…  ·  GET v1/performance-actions/counts?periodId[eq]
// api.js GET'te reject ETMEZ — hata sinyali res.data.status (false/eksik → panel hata durumu).

// E02 Süreç Takibi bağlantıları (Faz 2 Plan 4, spec §19/4): components/performance/processHealthLinks.js → processHealthHref(filters, hedef).
// Mevcut ortak süzgeçler korunur; görev listesi her zaman yalnız zorunlu görevler.

export const KPI_CODES = ['M01', 'M02', 'M04', 'pm.managerCompletion', 'pm.calibrationPending'];
export const LIVE_INSIGHT_STATUSES = 'new,reviewing,actioned';
export const REVIEW_RULE = 'pm.review.attention';
export const TOP_DISTRIBUTION = 7;
export const TOP_UNITS = 6;
export const LIST_SIZE = 5;
export const AI_CARDS = 3;

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

export class PanelError extends Error {}

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new PanelError(res?.data?.message || 'error');
  return data;
}

const pageRows = (data) => (Array.isArray(data) ? data : (data?.data || []));
const total = (data) => (Array.isArray(data) ? data.length : (data?.totalRowCount ?? pageRows(data).length));

export const fetchMetrics = (filters, periodId, codes, compare) => get('/v1/performance-analytics/metrics', {
  ...toAnalyticsParams(filters, periodId), codes: codes.join(','), ...(compare ? { compare } : {}),
});

export const fetchBreakdown = (filters, codes, dimension) => get('/v1/performance-analytics/breakdown', {
  ...toAnalyticsParams(filters), codes: codes.join(','), dimension,
});

export const fetchOverdueTasks = async (filters) => {
  const data = await get('/v1/performance-analytics/tasks', {
    // Sayı = liste (spec §16/5, §19/2): "Tümünü Gör (N)" E02 listesiyle aynı küme — yalnız zorunlu görevler.
    ...toAnalyticsParams(filters), overdue: 'true', mandatory: 'true', sort: 'overdueDays', order: 'desc', pageNumber: 1, pageSize: LIST_SIZE,
  });
  return { rows: pageRows(data), total: total(data) };
};

export const fetchInsights = async (params) => {
  const data = await get('/v1/performance-insights', params);
  return { rows: pageRows(data), total: total(data) };
};

export const fetchActions = async (periodId, statuses) => {
  const data = await get('/v1/performance-actions', {
    'periodId[eq]': periodId, 'status[in]': statuses, pageNumber: 1, pageSize: LIST_SIZE,
  });
  return { rows: pageRows(data), total: total(data) };
};

export const fetchActionCounts = (periodId) => get('/v1/performance-actions/counts', { 'periodId[eq]': periodId });

export const fetchPeriods = async () => {
  const res = await api.get('/v1/evaluation-periods', { params: { pageSize: 200, orderBy: 'startDate', orderDir: 'desc' } });
  return pageRows(payload(res) || []);
};

/**
 * Panel başına bağımsız yükleme (spec §6.5): her panel kendi isteğini atar; hatası yalnız kendisini etkiler, "Tekrar Dene" yalnız
 * onu yeniler. Eski bir isteğin geç gelen yanıtı yeni süzgeçlerin verisini ezmez.
 */
export function usePanel(fetcher, deps, enabled = true) {
  const [state, setState] = useState({ loading: !!enabled, error: false, data: null });
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    if (!enabled) { setState({ loading: false, error: false, data: null }); return; }
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: false }));
    Promise.resolve()
      .then(fetcher)
      .then((data) => { if (mine === seq.current) setState({ loading: false, error: false, data }); })
      .catch(() => { if (mine === seq.current) setState({ loading: false, error: true, data: null }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt, enabled]);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { ...state, retry };
}

// ---------------------------------------------------------------- KPI yardımcıları
export const metricOf = (list, code) => (list || []).find((m) => m.metricCode === code) || null;

// Payda 0 (ya da değer yok) → "Veri Yok" (spec §6.3).
export const hasValue = (m) => !!m && m.value != null && !(m.denominator != null && Number(m.denominator) === 0);

// Fark: karşılaştırma dönemine (period) ya da son 1 haftaya (7d) göre. Sonuç { delta } ya da { reason }.
export function periodDiff(current, comparison) {
  if (!comparison) return { reason: 'noComparePeriod' };
  if (!hasValue(current) || !hasValue(comparison)) return { reason: 'noData' };
  return { delta: round2(Number(current.value) - Number(comparison.value)) };
}

export function weekDiff(current) {
  if (!hasValue(current)) return { reason: 'noData' };
  if (current.comparison?.value == null) return { reason: current.comparisonUnavailableReason || 'noSnapshot' };
  return { delta: round2(Number(current.value) - Number(current.comparison.value)) };
}

export const round2 = (v) => Math.round(Number(v) * 100) / 100;

// Yönü iyi olan artış: tamamlama metriklerinde artış iyi (yeşil), geciken/bekleyende kötü (kırmızı).
export const trendTone = (delta, higherIsBetter) => {
  if (delta == null || delta === 0) return 'neutral';
  return (delta > 0) === higherIsBetter ? 'good' : 'bad';
};

export const formatNumber = (v, lang, digits = 2) => (v == null ? '' : new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)));

// ---------------------------------------------------------------- panel yardımcıları
// Geciken dağılımı: büyükten küçüğe ilk N kalem, kalanı tek "Diğer" kalemi (spec §6.4).
export function topWithOther(items, n = TOP_DISTRIBUTION) {
  const sorted = [...(items || [])].filter((x) => Number(x.value) > 0).sort((a, b) => Number(b.value) - Number(a.value));
  if (sorted.length <= n) return { items: sorted, other: null };
  const rest = sorted.slice(n);
  return { items: sorted.slice(0, n), other: rest.reduce((s, x) => s + Number(x.value), 0) };
}

// Birim tamamlama: M02'ye göre ARTAN (en geride kalan birim üstte), ilk 6; M02'si olmayan birim sona.
export function lowestUnits(cells, n = TOP_UNITS) {
  return [...(cells || [])]
    .map((c) => ({ cell: c, m01: metricOf(c.metrics, 'M01'), m02: metricOf(c.metrics, 'M02'), m04: metricOf(c.metrics, 'M04') }))
    .sort((a, b) => {
      const av = hasValue(a.m02) ? Number(a.m02.value) : Number.POSITIVE_INFINITY;
      const bv = hasValue(b.m02) ? Number(b.m02.value) : Number.POSITIVE_INFINITY;
      return av - bv || String(a.cell.label || '').localeCompare(String(b.cell.label || ''));
    })
    .slice(0, n);
}

// Panel meta: yanıtın hesaplanma anı ve bayatlığı → şeritteki "Son güncelleme" (en eski) ve "Eski Veri".
export function oldestCalculatedAt(metas) {
  const times = Object.values(metas || {}).map((m) => m?.calculatedAt).filter(Boolean).sort();
  return times[0] || null;
}

export const anyStale = (metas) => Object.values(metas || {}).some((m) => m?.stale);

export const metaOfMetrics = (list) => ({
  calculatedAt: (list || []).map((m) => m.calculatedAt).filter(Boolean).sort()[0] || null,
  stale: (list || []).some((m) => m.stale),
});

export const metaOfBreakdown = (bd) => ({ calculatedAt: bd?.calculatedAt || null, stale: !!bd?.stale });
